import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Mail, Send, Play, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { cn } from "@/lib/utils";

export default function SettingsModal({ open, onOpenChange }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["notifications"],
    queryFn: api.getNotifications,
    enabled: open,
  });

  const [enabled, setEnabled] = useState(true);
  const [recipientsText, setRecipientsText] = useState("");
  const [testEmail, setTestEmail] = useState("");

  useEffect(() => {
    if (data && open) {
      setEnabled(!!data.enabled);
      setRecipientsText((data.recipients || []).join(", "));
    }
  }, [data, open]);

  const parsedRecipients = useMemo(
    () =>
      recipientsText
        .split(/[,\n;]/)
        .map((s) => s.trim())
        .filter(Boolean),
    [recipientsText]
  );

  const invalidRecipients = parsedRecipients.filter(
    (e) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)
  );

  const saveM = useMutation({
    mutationFn: () =>
      api.setNotifications({ enabled, recipients: parsedRecipients }),
    onSuccess: (d) => {
      qc.setQueryData(["notifications"], d);
      toast.success("Reminder settings saved");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const testM = useMutation({
    mutationFn: () => api.sendTestEmail(testEmail),
    onSuccess: () => toast.success(`Test email sent to ${testEmail}`),
    onError: (e) =>
      toast.error(e?.response?.data?.detail || "Failed to send test"),
  });

  const runNowM = useMutation({
    mutationFn: () => api.runReminders({ dryRun: false }),
    onSuccess: (r) => {
      const c = r.sent ?? 0;
      toast.success(`Dispatched ${c} reminder email${c === 1 ? "" : "s"}`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Run failed"),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg p-0 gap-0 border-[#E2E8F0]"
        data-testid={TID.settingsModal}
      >
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-[#E2E8F0]">
          <DialogTitle className="text-base font-semibold text-[#0F172A] flex items-center gap-2">
            <Mail className="h-4 w-4 text-[#64748B]" />
            Reminder Settings
          </DialogTitle>
          <DialogDescription className="text-xs text-[#64748B]">
            Firm-wide email digests fire daily at 08:00 IST for tasks 7 days, 1
            day, on the due date, and 3 days after (if still pending).
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 py-4 flex flex-col gap-4">
          {isLoading ? (
            <div className="text-xs text-[#64748B]">Loading…</div>
          ) : (
            <>
              <label className="inline-flex items-center gap-2 text-sm text-[#0F172A]">
                <input
                  type="checkbox"
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                  className="h-4 w-4 accent-[#0F172A]"
                  data-testid={TID.settingsEnabled}
                />
                <span className="font-medium">Enable reminder emails</span>
              </label>

              <label className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-[#334155]">
                  Firm recipients
                </span>
                <textarea
                  value={recipientsText}
                  onChange={(e) => setRecipientsText(e.target.value)}
                  rows={3}
                  placeholder="[email protected], [email protected]"
                  className={cn(
                    "w-full px-3 py-2 rounded-md border bg-white text-sm placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#0F172A]/10",
                    invalidRecipients.length > 0
                      ? "border-[#DC2626]"
                      : "border-[#E2E8F0]"
                  )}
                  data-testid={TID.settingsRecipients}
                />
                <div className="text-[11px] text-[#64748B]">
                  Comma or newline separated. Each client's contact email (if set)
                  is added automatically to their own digest.
                </div>
                {invalidRecipients.length > 0 && (
                  <div className="text-[11px] text-[#DC2626]">
                    Invalid: {invalidRecipients.join(", ")}
                  </div>
                )}
              </label>

              <div className="rounded-md border border-[#E2E8F0] bg-[#F8FAFC] p-3 flex flex-col gap-2">
                <div className="text-xs font-medium text-[#334155]">
                  Send a test email
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="email"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    placeholder="[email protected]"
                    className="flex-1 h-9 px-3 rounded-md border border-[#E2E8F0] bg-white text-sm"
                    data-testid={TID.settingsTestEmail}
                  />
                  <button
                    type="button"
                    onClick={() => testM.mutate()}
                    disabled={
                      testM.isPending ||
                      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(testEmail)
                    }
                    data-testid={TID.settingsSendTest}
                    className="h-9 px-3 rounded-md bg-[#0F172A] text-white text-xs font-medium hover:bg-[#1E293B] disabled:opacity-50 inline-flex items-center gap-1.5"
                  >
                    {testM.isPending ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Send className="h-3.5 w-3.5" />
                    )}
                    Send test
                  </button>
                </div>
              </div>

              <div className="rounded-md border border-[#E2E8F0] p-3 flex items-center justify-between gap-2">
                <div>
                  <div className="text-xs font-medium text-[#334155]">
                    Trigger reminders now
                  </div>
                  <div className="text-[11px] text-[#64748B]">
                    Dispatches today's digest to all recipients using the current
                    settings.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => runNowM.mutate()}
                  disabled={runNowM.isPending || !enabled}
                  data-testid={TID.settingsRunNow}
                  className="h-9 px-3 rounded-md border border-[#E2E8F0] bg-white text-xs font-medium text-[#334155] hover:bg-[#F8FAFC] disabled:opacity-50 inline-flex items-center gap-1.5"
                >
                  {runNowM.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Play className="h-3.5 w-3.5" />
                  )}
                  Run now
                </button>
              </div>
            </>
          )}
        </div>

        <DialogFooter className="px-6 py-4 border-t border-[#E2E8F0] bg-[#F8F9FA] rounded-b-lg">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="h-9 px-3 rounded-md border border-[#E2E8F0] bg-white text-sm text-[#334155] hover:bg-[#F1F5F9]"
          >
            <X className="h-3.5 w-3.5" />
            Close
          </button>
          <button
            type="button"
            onClick={() => saveM.mutate()}
            disabled={saveM.isPending || invalidRecipients.length > 0}
            data-testid={TID.settingsSave}
            className="h-9 px-3.5 rounded-md bg-[#0F172A] text-white text-sm font-medium hover:bg-[#1E293B] disabled:opacity-50 inline-flex items-center gap-2"
          >
            {saveM.isPending && (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            )}
            Save settings
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
